import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  useGetAllDepartments,
  useGetAllLocationsDropdown,
  useGetDepartmentById,
  useGetAllBuildingsById,
} from "../../../hooks/common/query";
import { mapToOptions } from "../../../lib/functions";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { DepartmentsSchema } from "../../../schemas/departmentsSchema";
import DepartmentsTable from "../../../components/table/DepartmentsTable";
import useAuthStore from "../../../store/Auth";
import {
  useAddUpdateDepartment,
  useIsDepartmentExists,
  useSyncDepartments,
} from "../../../hooks/common/mutation";
import { PAGE_SIZE } from "../../../constants/common";
import BackButton from "../../../components/button/BackButton";

interface LocationData {
  departmentEn: string;
  departmentAr: string;
  description?: string | null;
  location: string;
  building: string | null;
}

const Departments = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
    watch,
  } = useFormWithSchema(DepartmentsSchema);

  const [allDepartments, setAllDepartments] = useState([]);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [selectedDepartmentId, setSelectedDepartmentId] = useState(0);

  const { mutateAsync: IsDepartmentExists } = useIsDepartmentExists();
  const { data: departments } = useGetAllDepartments({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (departments) {
      setAllDepartments(departments.data || []);
      setPages(departments.pagination?.pages || 1);
      setCount(departments.pagination?.count || 0);
    }
  }, [setPages, departments]);

  const {
    mutateAsync: AddUpdateDepartment,
    isPending: isAddUpdateDepartmentLoading,
  } = useAddUpdateDepartment();

  const { mutateAsync: SyncDepartments } = useSyncDepartments();

  const user = useAuthStore((state) => state.user);

  const { data: selectedDepartment } = useGetDepartmentById({
    encryptedId: selectedDepartmentId ? selectedDepartmentId.toString() : "",
  });

  const { data: allLocations } = useGetAllLocationsDropdown();

  const selectedLocationId = watch("location");
  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  useEffect(() => {
    if (selectedDepartment) {
      setValue("departmentEn", selectedDepartment.s_DepartmentName_En);
      setValue("departmentAr", selectedDepartment.s_DepartmentName_Ar);
      setValue("description", selectedDepartment.s_Description);
      setValue("location", selectedDepartment.n_LocationID?.toString());
      setValue("building", selectedDepartment.n_BuildingID?.toString());
    }
  }, [selectedDepartment, setValue]);

  const onSubmit = useCallback(
    async (data: LocationData) => {
      try {
        const isExistBody = {
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
          n_DepartmentID: selectedDepartmentId,
          s_DepartmentName_En: data.departmentEn,
          s_DepartmentName_Ar: data.departmentAr,
        };
        const { isExists } = await IsDepartmentExists(isExistBody);

        if (isExists) {
          toast.error(t("departmentExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));

        console.error("Error checking if department exists", error);
        return;
      }

      try {
        if (user?.user_id) {
          const body = {
            n_DepartmentID: 0,
            s_DepartmentName_En: data.departmentEn,
            s_DepartmentName_Ar: data.departmentAr,
            n_LocationID: Number(data.location),
            n_BuildingID: Number(data.building),
            s_Description: data.description,
            n_UserID: user?.user_id,
          };
          if (selectedDepartmentId === 0) {
            await AddUpdateDepartment(body);
          } else {
            const updateBody = {
              ...body,
              n_DepartmentID: selectedDepartmentId,
            };
            await AddUpdateDepartment(updateBody);
          }
        }
        setSelectedDepartmentId(0);
        if (selectedDepartmentId === 0) {
          toast.success(t("departmentsAdded"));
        } else {
          toast.success(t("departmentsUpdated"));
        }

        resetField("departmentAr");
        resetField("departmentEn");
        resetField("description");
        resetField("location");
        resetField("building");
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error adding/updating department", error);
      }
    },
    [
      AddUpdateDepartment,
      t,
      IsDepartmentExists,
      resetField,
      selectedDepartmentId,
      user?.user_id,
    ]
  );

  const handleSyncDepartments = useCallback(async () => {
    try {
      await SyncDepartments();
      toast.success(t("syncDepartmentsInitiated"));
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  }, [SyncDepartments, t]);

  const onClear = useCallback(() => {
    setSelectedDepartmentId(0);

    resetField("departmentAr");
    resetField("departmentEn");
    resetField("description");
    resetField("location");
    resetField("building");
  }, [resetField]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("departments")}
          </h1>

          <div className="flex items-center gap-2">
            {/* <Button
              type="button"
              isLoading={isSyncDepartmentsLoading}
              onClick={handleSyncDepartments}
              className="hidden sm:block bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
            >
              {t("syncDepartments")}
            </Button> */}

            <BackButton />
          </div>
        </div>

        {/* <Button
          type="button"
          isLoading={isSyncDepartmentsLoading}
          onClick={handleSyncDepartments}
          className="sm:hidden bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white max-w-max"
        >
          {t("syncDepartments")}
        </Button> */}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
              <Controller
                name="departmentEn"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("departmentNameEn")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                )}
              />

              <Controller
                name="departmentAr"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("departmentNameAr")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      value
                        ? locations?.find(
                            (item) => item.value === Number(value)
                          )?.labelEn || ""
                        : ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                    required
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    required
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <TextInput
                      label={t("description")}
                      onChange={onChange}
                      value={value || ""}
                      error={errors?.[name]?.message}
                    />
                  );
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button isLoading={isAddUpdateDepartmentLoading}>
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>

          <DepartmentsTable
            departments={allDepartments}
            setSelectedDepartmentId={setSelectedDepartmentId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>
    </>
  );
};

export default Departments;
